"use client";

import { Book, Checkmark, Close, Document, Search } from "@carbon/react/icons";
import type { DocumentIndexLayer, IndexLayerId } from "@/lib/ui/document-index";
import {
  INDEX_LAYER_LABELS,
  INDEX_LAYER_ORDER,
  indexErrorCopy,
} from "@/lib/ui/document-index";
import type { DocumentIndexProgressView } from "@/lib/ui/document-progress";
import { copyTextToClipboard } from "@/lib/ui/clipboard";

const ICONS: Record<IndexLayerId, typeof Document> = {
  text: Document,
  vectors: Search,
  facts: Book,
};

type IconVariant = {
  variant: "icon";
  layers: DocumentIndexLayer[];
  size?: number;
};

type HeadVariant = {
  variant: "head";
};

type MarksVariant = {
  variant: "marks";
  layers: DocumentIndexLayer[];
  binary?: boolean;
};

type ProgressVariant = {
  variant: "progress";
  progress: DocumentIndexProgressView;
};

type DocumentIndexStatusProps =
  | IconVariant
  | HeadVariant
  | MarksVariant
  | ProgressVariant;

export function DocumentIndexStatus(props: DocumentIndexStatusProps) {
  if (props.variant === "icon") {
    const size = props.size ?? 16;
    return (
      <span className="document-index-icons">
        {props.layers.map((item) => {
          const Icon = ICONS[item.id];
          return (
            <span
              key={item.id}
              className={`document-index-icon document-index-icon-${item.tone}`}
              title={item.title}
              aria-label={item.title}
            >
              <Icon size={size} />
            </span>
          );
        })}
      </span>
    );
  }

  if (props.variant === "head") {
    return (
      <span className="document-row-index" aria-label="Индекс">
        {INDEX_LAYER_ORDER.map((id) => {
          const Icon = ICONS[id];
          return (
            <span
              key={id}
              title={INDEX_LAYER_LABELS[id]}
              aria-label={INDEX_LAYER_LABELS[id]}
            >
              <Icon size={16} />
            </span>
          );
        })}
      </span>
    );
  }

  if (props.variant === "progress") {
    const { progress } = props;
    const label = progress.eta
      ? `${progress.label} · ${progress.eta}`
      : progress.label;
    return (
      <span
        className={`document-row-progress${progress.waiting ? " is-wait" : ""}`}
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={progress.waiting ? undefined : progress.percent}
      >
        <span className="document-row-progress-meta">{label}</span>
        <span className="document-row-progress-track">
          <span
            className="document-row-progress-fill"
            style={
              progress.waiting ? undefined : { width: `${progress.percent}%` }
            }
          />
        </span>
      </span>
    );
  }

  const byId = new Map(props.layers.map((item) => [item.id, item]));
  return (
    <span className="document-row-index">
      {INDEX_LAYER_ORDER.map((id) => {
        const layer = byId.get(id);
        const tone = layer?.tone ?? "alert";
        const Icon = props.binary
          ? tone === "neutral"
            ? Checkmark
            : Close
          : tone === "neutral"
            ? Checkmark
            : tone === "busy"
              ? ICONS[id]
              : Close;
        const label = layer?.title ?? INDEX_LAYER_LABELS[id];
        const canCopy = tone === "alert";
        // Native button: copy-hit target inside a row, not a page action.
        const Mark = canCopy ? "button" : "span";
        return (
          <Mark
            key={id}
            type={canCopy ? "button" : undefined}
            className={`document-index-mark document-index-mark-${tone}`}
            title={canCopy ? `${label}. ${indexErrorCopy.copyHint}` : label}
            aria-label={canCopy ? `${label}. ${indexErrorCopy.copyHint}` : label}
            onClick={
              canCopy
                ? (event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    void copyTextToClipboard(label);
                  }
                : undefined
            }
          >
            <Icon size={16} />
          </Mark>
        );
      })}
    </span>
  );
}
